import React from 'react';

import type { IntlShape } from 'react-intl';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import commonMessages, {
	syncBlockMessages as messages,
	toolbarInsertBlockMessages,
} from '@atlaskit/editor-common/messages';
import { GapCursorSelection, Side } from '@atlaskit/editor-common/selection';
import type {
	Command,
	ExtractInjectionAPI,
	FloatingToolbarConfig,
	FloatingToolbarCustomRenderContext,
	FloatingToolbarItem,
} from '@atlaskit/editor-common/types';
import { FloatingToolbarButton as Button } from '@atlaskit/editor-common/ui';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { findDomRefAtPos } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { akEditorSelectedNodeClassName } from '@atlaskit/editor-shared-styles/consts';
import { SyncBlockError } from '@atlaskit/editor-synced-block-provider';
import type { SyncBlockStoreManager } from '@atlaskit/editor-synced-block-provider';
import CopyIcon from '@atlaskit/icon/core/copy';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import LinkBrokenIcon from '@atlaskit/icon/core/link-broken';
import MegaphoneIcon from '@atlaskit/icon/core/megaphone';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import {
	copySyncedBlockReferenceToClipboard,
	editSyncedBlockSource,
	removeSyncedBlock,
	unsync,
} from '../editor-commands';
import { findSyncBlockOrBodiedSyncBlock, isBodiedSyncBlockNode } from '../pm-plugins/utils/utils';
import type { SyncedBlockFeedbackContext, SyncedBlockPlugin } from '../syncedBlockPluginType';
import { SYNCED_BLOCK_BUTTON_TEST_ID } from '../types';

import { SyncedLocationDropdown, SyncedLocationDropdownWithCount } from './SyncedLocationDropdown';

export const getToolbarConfig = (
	state: EditorState,
	intl: IntlShape,
	api: ExtractInjectionAPI<SyncedBlockPlugin> | undefined,
	syncBlockStore: SyncBlockStoreManager,
	isLivePage?: boolean,
	onGiveFeedback?: (context: SyncedBlockFeedbackContext) => void,
): FloatingToolbarConfig | undefined => {
	const syncBlockObject = findSyncBlockOrBodiedSyncBlock(state.schema, state.selection);
	if (!syncBlockObject) {
		return;
	}

	if (syncBlockStore.sourceManager.isPendingCreation(syncBlockObject.node.attrs.resourceId)) {
		return;
	}

	const isSyncBlockActivationEnabled = expValEquals(
		'platform_editor_sync_block_activation',
		'isEnabled',
		true,
	);

	const syncBlockInstance = syncBlockStore.referenceManager.getFromCache(
		syncBlockObject.node.attrs.resourceId,
	);

	const isUnsyncedBlock = syncBlockInstance?.error?.type === SyncBlockError.NotFound;
	const isErroredBlock = syncBlockInstance?.error;

	const {
		schema: {
			nodes: { bodiedSyncBlock },
		},
	} = state;
	const isBodiedSyncBlock = isBodiedSyncBlockNode(syncBlockObject.node, bodiedSyncBlock);
	const { resourceId, localId } = syncBlockObject.node.attrs;

	const { formatMessage } = intl;
	const nodeType = syncBlockObject.node.type;
	const hoverDecoration = api?.decorations?.actions.hoverDecoration;
	const hoverDecorationProps = (nodeType: NodeType | NodeType[], className?: string) => ({
		onMouseEnter: hoverDecoration?.(nodeType, true, className),
		onMouseLeave: hoverDecoration?.(nodeType, false, className),
		onFocus: hoverDecoration?.(nodeType, true, className),
		onBlur: hoverDecoration?.(nodeType, false, className),
	});

	const items: Array<FloatingToolbarItem<Command>> = [];
	if (isUnsyncedBlock) {
		const deleteButton: FloatingToolbarItem<Command> = {
			type: 'button',
			title: formatMessage(commonMessages.delete),
			onClick: removeSyncedBlock(api),
			icon: DeleteIcon,
			testId: SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarReferenceDelete,
			...hoverDecorationProps(nodeType, akEditorSelectedNodeClassName),
		};

		items.push(deleteButton);
	} else {
		// When the synced block activation experiment is enabled, the copy action is
		// re-framed as the primary "Copy to sync" action: it shows a visible label and
		// is moved to the first toolbar position (before the synced-location info). See
		// EDITOR-8060.
		const copyButton: FloatingToolbarItem<Command> = {
			id: 'editor.syncedBlock.copy',
			type: 'button',
			appearance: 'subtle',
			icon: CopyIcon,
			title: isSyncBlockActivationEnabled
				? formatMessage(messages.copyToSyncLabel)
				: formatMessage(messages.copySyncBlockLabel),
			showTitle: isSyncBlockActivationEnabled,
			tooltipContent: isSyncBlockActivationEnabled
				? undefined
				: formatMessage(messages.copySyncedBlockTooltip),
			testId: isSyncBlockActivationEnabled
				? SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarCopy
				: undefined,
			onClick: copySyncedBlockReferenceToClipboard(
				syncBlockStore,
				INPUT_METHOD.SYNCED_BLOCK_TB,
				api,
				isLivePage,
			),
			...hoverDecorationProps(nodeType, akEditorSelectedNodeClassName),
		};

		// In the activation experience the primary "Copy to sync" action leads the
		// toolbar, ahead of the informational synced-location dropdown.
		if (isSyncBlockActivationEnabled) {
			items.push(copyButton);
		}

		if (!isErroredBlock) {
			const syncedLocation: FloatingToolbarItem<Command> = {
				type: 'custom',
				fallback: [],
				render: (
					_view,
					_idx,
					_dispatchAnalyticsEvent,
					floatingToolbarRenderContext?: FloatingToolbarCustomRenderContext,
				) => {
					if (isSyncBlockActivationEnabled) {
						return (
							<SyncedLocationDropdownWithCount
								key={`${resourceId}:${localId}:${isBodiedSyncBlock}`}
								syncBlockStore={syncBlockStore}
								resourceId={resourceId}
								localId={localId}
								intl={intl}
								isSource={isBodiedSyncBlock}
								api={api}
								floatingToolbarRenderContext={floatingToolbarRenderContext}
							/>
						);
					}

					return (
						<SyncedLocationDropdown
							syncBlockStore={syncBlockStore}
							resourceId={resourceId}
							localId={localId}
							intl={intl}
							isSource={isBodiedSyncBlock}
							api={api}
							floatingToolbarRenderContext={floatingToolbarRenderContext}
						/>
					);
				},
			};

			const unsyncButton: FloatingToolbarItem<Command> = {
				type: 'custom',
				fallback: [],
				render: (view) => {
					return (
						<Button
							areAnyNewToolbarFlagsEnabled={true}
							disabled={syncBlockInstance?.data?.status === 'unpublished'}
							icon={<LinkBrokenIcon label="" />}
							title={formatMessage(messages.unsyncButton)}
							// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onClick={() => unsync(syncBlockStore, isBodiedSyncBlock, view)}
							testId={
								isBodiedSyncBlock
									? SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarSourceUnsync
									: SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarReferenceUnsync
							}
						/>
					);
				},
			};
			items.push(syncedLocation, unsyncButton);
		}

		// Outside the activation experience the copy button keeps its original
		// (icon-only, secondary) position after the synced-location info.
		if (!isSyncBlockActivationEnabled) {
			items.push(copyButton);
		}

		const disabled = !syncBlockStore.referenceManager.getSyncBlockURL(
			syncBlockObject.node.attrs.resourceId,
		);

		if (!isBodiedSyncBlock) {
			const editSourceButton: FloatingToolbarItem<Command> = {
				id: 'editor.syncedBlock.editSource',
				type: 'button',
				disabled,
				appearance: 'subtle',
				icon: EditIcon,
				title: formatMessage(messages.editSourceLabel),
				showTitle: false,
				tooltipContent: disabled
					? formatMessage(messages.editSourceTooltipDisabled)
					: formatMessage(messages.editSourceTooltip),
				onClick: editSyncedBlockSource(syncBlockStore, api),
				...hoverDecorationProps(nodeType, akEditorSelectedNodeClassName),
			};
			items.push(editSourceButton);
		}

		if (!isSyncBlockActivationEnabled) {
			const separator: FloatingToolbarItem<Command> = {
				type: 'separator',
			};

			const deleteButton: FloatingToolbarItem<Command> = {
				id: 'editor.syncedBlock.delete',
				type: 'button',
				appearance: 'danger',
				title: formatMessage(commonMessages.delete),
				showTitle: false,
				tooltipContent: formatMessage(commonMessages.delete),
				onClick: removeSyncedBlock(api),
				icon: DeleteIcon,
				testId: isBodiedSyncBlock
					? SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarSourceDelete
					: SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarReferenceDelete,
				...hoverDecorationProps(nodeType),
			};
			items.push(separator, deleteButton);
		} else {
			// testId is required to show focus on trigger button on ESC key press
			// see hideOnEsc in platform/packages/editor/editor-plugin-floating-toolbar/src/ui/Dropdown.tsx
			const testId = isBodiedSyncBlock
				? SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarSourceOverflowTrigger
				: SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarReferenceOverflowTrigger;

			const overflowMenuConfig: FloatingToolbarItem<Command>[] = [
				{
					type: 'overflow-dropdown',
					testId,
					options: [
						...(isSyncBlockActivationEnabled && onGiveFeedback
							? [
									{
										title: formatMessage(toolbarInsertBlockMessages.feedbackDialog),
										icon: <MegaphoneIcon label="" />,
										onClick: () => {
											api?.core.actions.execute(({ tr }) =>
												tr.setSelection(
													new GapCursorSelection(
														tr.doc.resolve(syncBlockObject.pos + syncBlockObject.node.nodeSize),
														Side.RIGHT,
													),
												),
											);
											onGiveFeedback({
												blockType: isBodiedSyncBlock ? 'source' : 'reference',
												entryPoint: 'overflow-menu',
											});
											return true;
										},
									},
								]
							: []),
						{
							title: formatMessage(commonMessages.delete),
							onClick: removeSyncedBlock(api),
							icon: <DeleteIcon label="" />,
							testId: isBodiedSyncBlock
								? SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarSourceDelete
								: SYNCED_BLOCK_BUTTON_TEST_ID.syncedBlockToolbarReferenceDelete,
							...hoverDecorationProps(nodeType),
						},
					],
				},
			];
			items.push(...overflowMenuConfig);
		}
	}

	const getDomRef = (editorView: EditorView) => {
		const domAtPos = editorView.domAtPos.bind(editorView);
		const element = findDomRefAtPos(syncBlockObject.pos, domAtPos) as HTMLDivElement;
		return element;
	};

	return {
		title: 'Synced Block floating controls',
		getDomRef,
		nodeType,
		items,
		scrollable: true,
		groupLabel: formatMessage(messages.syncBlockGroup),
		visible: api?.connectivity?.sharedState.currentState()?.mode !== 'offline',
	};
};
